import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  AppBar,
  Toolbar,
  Button,
  Tab,
  Tabs,
  Grid,
  Slider,
  Chip,
  Divider,
  Alert,
  LinearProgress,
  TextField,
  InputAdornment
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BarChartIcon from '@mui/icons-material/BarChart';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BeltGrid from './BeltGrid';
import BeltSchedulePanel from './BeltSchedulePanel';
import DateTabs from './DateTabs';
import ConflictsView from './ConflictsView';
import HourlyDistributionView from './HourlyDistributionView';
import AssignmentAuditView from './AssignmentAuditView';
import OptimizationProgress from './OptimizationProgress';
import { usePiers } from '../context/PiersContext';
import { exportToExcel } from '../utils/excelParser';

const PiersView = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const configChangeTimerRef = useRef(null);
  const [showProgress, setShowProgress] = useState(false);
  const [progressMode, setProgressMode] = useState('optimize');
  const {
    importedFileName,
    beltAssignments,
    configuration,
    updateConfiguration,
    flights,
    flightsByDate,
    setAllBeltAssignments,
    importFlights,
    selectedDate
  } = usePiers();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleExport = () => {
    exportToExcel(beltAssignments, configuration, selectedDate);
  };
  
  // Auto re-optimize when configuration changes (debounced)
  useEffect(() => {
    if (flights.length > 0 && importedFileName) {
      if (configChangeTimerRef.current) {
        clearTimeout(configChangeTimerRef.current);
      }
      configChangeTimerRef.current = setTimeout(() => {
        configChangeTimerRef.current = null;
        reprocessFlights();
      }, 1000);
      return () => {
        if (configChangeTimerRef.current) {
          clearTimeout(configChangeTimerRef.current);
          configChangeTimerRef.current = null;
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration]);

  const handleConfigChange = (field, value) => {
    updateConfiguration({ [field]: value });
  };

  // Optimize one date's flights via the backend. Returns { assignments, warning }.
  // Throws only when the request errors or no flight could be placed at all;
  // a degraded-but-usable result comes back with a non-blocking warning.
  const optimizeDateFlights = async (dateFlights, date) => {
    const response = await fetch('/api/belt/optimize', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        flights: dateFlights,
        configuration: configuration
      })
    });

    const result = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(result.error || `Failed to optimize belt assignments for ${date}`);
    }

    // Convert numeric belt keys to strings for consistency
    const assignments = {};
    Object.keys(result.assignments || {}).forEach(belt => {
      assignments[String(belt)] = result.assignments[belt];
    });

    const totalAssigned = Object.values(assignments)
      .reduce((n, fs) => n + (Array.isArray(fs) ? fs.length : 0), 0);

    // Truly nothing placed — this is a real failure worth blocking on.
    if (totalAssigned === 0) {
      throw new Error(
        result.fallbackReason
          ? `Optimization failed for ${date}: ${result.fallbackReason}`
          : `No flights could be assigned for ${date}. Check belt schedules — ` +
            'every departure time needs at least one open belt.'
      );
    }

    let warning = '';
    if (result.fallbackReason) {
      warning = `${date}: ${result.fallbackReason}`;
    }

    const relaxed = result.metrics?.relaxed_flights;
    if (relaxed && relaxed.length > 0) {
      console.warn(`Flights placed outside configured belt hours for ${date}:`, relaxed);
      const extra = `${date}: ${relaxed.length} flight(s) placed outside configured belt hours ` +
        `(no open belt at their departure time).`;
      warning = warning ? `${warning} ${extra}` : extra;
    }

    return { assignments, warning };
  };

  const reprocessFlights = async () => {
    if (!importedFileName || Object.keys(flightsByDate).length === 0) return;

    try {
      setError('');
      setWarning('');
      setProgressMode('reoptimize');
      setShowProgress(true);

      // Re-optimize every date from the original imported flights, so no date
      // keeps assignments computed under an older configuration and currently
      // unassigned flights are never dropped.
      const allAssignments = {};
      const warnings = [];
      for (const [date, dateFlights] of Object.entries(flightsByDate)) {
        const { assignments, warning: w } = await optimizeDateFlights(dateFlights, date);
        allAssignments[date] = assignments;
        if (w) warnings.push(w);
      }

      setAllBeltAssignments(allAssignments);
      setWarning(warnings.join(' '));
      setShowProgress(false);
    } catch (err) {
      console.error('Re-optimization failed:', err);
      setError(err.message || 'Failed to re-optimize assignments. Please ensure the backend is running.');
      setShowProgress(false);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;

    setLoading(true);
    setError('');
    setWarning('');

    try {
      // Show progress dialog
      setProgressMode('optimize');
      setShowProgress(true);

      // Dynamic import to avoid circular dependencies
      const { parseGateSheet } = await import('../utils/excelParser');

      // Parse the Excel file (now returns flights by date)
      const parsedFlightsByDate = await parseGateSheet(file);

      if (Object.keys(parsedFlightsByDate).length === 0) {
        throw new Error('No valid flight data found in the file');
      }

      // Don't import yet - wait until we have assignments

      // Optimize for each date
      const allAssignments = {};
      const warnings = [];

      for (const [date, dateFlights] of Object.entries(parsedFlightsByDate)) {
        const { assignments, warning: w } = await optimizeDateFlights(dateFlights, date);
        allAssignments[date] = assignments;
        if (w) warnings.push(w);
      }

      setWarning(warnings.join(' '));

      // Import flights with assignments
      importFlights(parsedFlightsByDate, file.name, allAssignments);

      // Close progress dialog after successful import
      setShowProgress(false);

    } catch (err) {
      setError(err.message || 'Failed to process file');
      setShowProgress(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  return (
    <Box sx={{
      minHeight: 'calc(100vh - 64px)',
      color: '#fff',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      zIndex: 1
    }}>
      {/* Frosted Glass AppBar */}
      <AppBar 
        position="static" 
        elevation={0}
        sx={{
          bgcolor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, color: '#C0C0C0', fontWeight: 600 }}>
            Pier Belt Management
          </Typography>
          {importedFileName && (
            <TextField
              size="small"
              placeholder="Flight #"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '1.1rem' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mr: 2,
                width: 150,
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  fontSize: '0.85rem',
                  bgcolor: 'rgba(255,255,255,0.05)',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                  '&:hover fieldset': { borderColor: 'rgba(249,182,18,0.4)' },
                  '&.Mui-focused fieldset': { borderColor: '#F9B612' },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(255,255,255,0.4)',
                  opacity: 1
                }
              }}
            />
          )}
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            disabled={!importedFileName}
            onClick={handleExport}
            sx={{
              backgroundColor: '#f9b612',
              color: '#000',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#e5a510'
              },
              '&:disabled': {
                backgroundColor: 'rgba(249, 182, 18, 0.3)',
                color: 'rgba(0, 0, 0, 0.3)'
              }
            }}
          >
            Export Excel
          </Button>
        </Toolbar>
      </AppBar>
      
      {/* Date Tabs - Only show when files are imported */}
      {importedFileName && <DateTabs />}

      {!importedFileName ? (
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Grid container spacing={4}>
            {/* Left Side - Parameters */}
            <Grid item xs={12} md={5}>
              <Paper 
                sx={{ 
                  p: 3, 
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 2
                }}
              >
                <Typography variant="h5" sx={{ color: '#C0C0C0', mb: 3, fontWeight: 600 }}>
                  Threshold Parameters
                </Typography>
                
                <Box sx={{ mb: 4 }}>
                  <Typography gutterBottom sx={{ color: '#fff' }}>
                    Active Belts: {configuration.activeBelts}
                  </Typography>
                  <Slider
                    value={configuration.activeBelts}
                    onChange={(e, value) => handleConfigChange('activeBelts', value)}
                    min={2}
                    max={24}
                    step={1}
                    marks
                    valueLabelDisplay="auto"
                    sx={{
                      color: '#F9B612',
                      '& .MuiSlider-track': { 
                        background: 'linear-gradient(to right, #FFD700, #FFA500)',
                        border: 'none'
                      },
                      '& .MuiSlider-rail': { 
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        height: 6
                      },
                      '& .MuiSlider-thumb': {
                        bgcolor: '#FFD700',
                        width: 20,
                        height: 20,
                        '&:hover': {
                          boxShadow: '0 0 0 8px rgba(255, 215, 0, 0.16)'
                        }
                      },
                      '& .MuiSlider-mark': {
                        bgcolor: 'rgba(255, 255, 255, 0.3)',
                        height: 10,
                        width: 2
                      }
                    }}
                  />
                </Box>

                <Box sx={{ mb: 4 }}>
                  <Typography gutterBottom sx={{ color: '#fff' }}>
                    Destination Conflict Window: {configuration.conflictWindow} hours
                  </Typography>
                  <Slider
                    value={configuration.conflictWindow}
                    onChange={(e, value) => handleConfigChange('conflictWindow', value)}
                    min={1}
                    max={6}
                    step={0.5}
                    marks={[
                      { value: 1, label: '1h' },
                      { value: 2, label: '2h' },
                      { value: 3, label: '3h' },
                      { value: 4, label: '4h' },
                      { value: 5, label: '5h' },
                      { value: 6, label: '6h' }
                    ]}
                    valueLabelDisplay="auto"
                    sx={{
                      color: '#F9B612',
                      '& .MuiSlider-track': { 
                        background: 'linear-gradient(to right, #FFD700, #FFA500)',
                        border: 'none'
                      },
                      '& .MuiSlider-rail': { 
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        height: 6
                      },
                      '& .MuiSlider-thumb': {
                        bgcolor: '#FFD700',
                        width: 20,
                        height: 20,
                        '&:hover': {
                          boxShadow: '0 0 0 8px rgba(255, 215, 0, 0.16)'
                        }
                      },
                      '& .MuiSlider-mark': {
                        bgcolor: 'rgba(255, 255, 255, 0.3)',
                        height: 10,
                        width: 2
                      },
                      '& .MuiSlider-markLabel': { color: 'rgba(255, 255, 255, 0.7)' }
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'center' }}>
                    <Chip 
                      label={`<${configuration.conflictWindow}h`} 
                      size="small" 
                      sx={{ 
                        bgcolor: '#FF5252', 
                        color: 'white',
                        fontWeight: 600,
                        boxShadow: '0 2px 4px rgba(255, 82, 82, 0.3)'
                      }} 
                    />
                    <Chip 
                      label={`${configuration.conflictWindow}h-${configuration.conflictWindow}h29m`} 
                      size="small" 
                      sx={{ 
                        bgcolor: '#FF8C00', 
                        color: 'white',
                        fontWeight: 600,
                        boxShadow: '0 2px 4px rgba(255, 140, 0, 0.3)'
                      }} 
                    />
                    <Chip 
                      label={`${configuration.conflictWindow}h30m-${configuration.conflictWindow}h59m`} 
                      size="small" 
                      sx={{ 
                        bgcolor: '#F9B612', 
                        color: 'black',
                        fontWeight: 600,
                        boxShadow: '0 2px 4px rgba(255, 215, 0, 0.3)'
                      }} 
                    />
                  </Box>
                </Box>

                <Box sx={{ mb: 4 }}>
                  <Typography gutterBottom sx={{ color: '#fff' }}>
                    Max Flights Per Belt Per Hour: {configuration.maxFlightsPerHour}
                  </Typography>
                  <Slider
                    value={configuration.maxFlightsPerHour}
                    onChange={(e, value) => handleConfigChange('maxFlightsPerHour', value)}
                    min={1}
                    max={10}
                    marks
                    valueLabelDisplay="auto"
                    sx={{
                      color: '#F9B612',
                      '& .MuiSlider-track': { 
                        background: 'linear-gradient(to right, #FFD700, #FFA500)',
                        border: 'none'
                      },
                      '& .MuiSlider-rail': { 
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        height: 6
                      },
                      '& .MuiSlider-thumb': {
                        bgcolor: '#FFD700',
                        width: 20,
                        height: 20,
                        '&:hover': {
                          boxShadow: '0 0 0 8px rgba(255, 215, 0, 0.16)'
                        }
                      },
                      '& .MuiSlider-mark': {
                        bgcolor: 'rgba(255, 255, 255, 0.3)',
                        height: 10,
                        width: 2
                      }
                    }}
                  />
                </Box>

                <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.08)' }} />

                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  These parameters will be applied when you import your gate sheet and can be adjusted after import.
                </Typography>
              </Paper>
            </Grid>

            {/* Right Side - Import */}
            <Grid item xs={12} md={7}>
              <Paper 
                sx={{ 
                  p: 3, 
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 2
                }}
              >
                <Typography variant="h5" gutterBottom sx={{ color: '#C0C0C0', mb: 3, fontWeight: 600 }}>
                  Paste Gate Sheet Data
                </Typography>
                
                <Box
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  sx={{
                    border: '2px dashed',
                    borderColor: dragActive ? '#FFD700' : 'rgba(255, 255, 255, 0.3)',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    bgcolor: dragActive ? 'rgba(255, 215, 0, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: '#FFD700',
                      bgcolor: 'rgba(255, 215, 0, 0.1)'
                    }
                  }}
                >
                  <input
                    type="file"
                    id="file-upload"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileInput}
                    style={{ display: 'none' }}
                    disabled={loading}
                  />
                  <label htmlFor="file-upload" style={{ cursor: 'pointer' }}>
                    <CloudUploadIcon sx={{ fontSize: 48, color: '#F9B612', mb: 2 }} />
                    <Typography variant="h6" gutterBottom sx={{ color: '#fff' }}>
                      Drag and drop your Gate Sheet here
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      or click to browse
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                      Supported formats: .xlsx, .xls, .csv
                    </Typography>
                  </label>
                </Box>

                {loading && (
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress 
                      sx={{ 
                        bgcolor: 'rgba(255, 215, 0, 0.1)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: '#FFD700'
                        }
                      }} 
                    />
                    <Typography variant="caption" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
                      Processing file...
                    </Typography>
                  </Box>
                )}

                {error && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mt: 2,
                      bgcolor: 'rgba(211, 47, 47, 0.1)',
                      color: '#ff6b6b',
                      border: '1px solid rgba(211, 47, 47, 0.3)',
                      '& .MuiAlert-icon': {
                        color: '#ff6b6b'
                      }
                    }}
                  >
                    {error}
                  </Alert>
                )}

                <Box sx={{ 
                  mt: 3, 
                  p: 2, 
                  bgcolor: 'rgba(255, 255, 255, 0.03)', 
                  borderRadius: 1,
                  border: '1px solid rgba(255, 255, 255, 0.08)'
                }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: '#F9B612' }}>
                    Expected Excel Format:
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    • Column H (8): Departure Time
                    <br />
                    • Column I (9): Flight Number
                    <br />
                    • Column J (10): Destination
                    <br />
                    • Column F (6): Aircraft Type
                    <br />
                    • Column A (1): Origin Status
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Belt Schedule Panel - Full Width Below */}
          <Box sx={{ mt: 4 }}>
            <BeltSchedulePanel />
          </Box>
        </Container>
      ) : (
        <>
          {/* Tabs for when data is loaded */}
          <Paper 
            elevation={0} 
            sx={{ 
              borderRadius: 0,
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  color: '#ccc',
                  '&:hover': {
                    color: '#FFD700'
                  },
                  '&.Mui-selected': {
                    color: '#FFD700'
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#FFD700'
                }
              }}
            >
              <Tab label="Belt Assignments" />
              <Tab label="Conflicts" />
              <Tab label="Hourly Distribution" icon={<BarChartIcon />} iconPosition="start" />
              <Tab label="Audit" icon={<AssignmentIcon />} iconPosition="start" />
            </Tabs>
          </Paper>

          {/* Parameter Bar when data is loaded */}
          <Paper 
            elevation={0} 
            sx={{ 
              borderRadius: 0,
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              p: 2
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={3}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#C0C0C0', mb: 1 }}>
                    Active Belts: {configuration.activeBelts}
                  </Typography>
                  <Slider
                    value={configuration.activeBelts}
                    onChange={(e, value) => handleConfigChange('activeBelts', value)}
                    min={2}
                    max={24}
                    step={1}
                    marks
                    size="small"
                    sx={{
                      color: '#F9B612',
                      '& .MuiSlider-track': { 
                        background: 'linear-gradient(to right, #FFD700, #FFA500)',
                        border: 'none'
                      },
                      '& .MuiSlider-rail': { 
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        height: 6
                      },
                      '& .MuiSlider-thumb': {
                        bgcolor: '#FFD700',
                        width: 20,
                        height: 20,
                        '&:hover': {
                          boxShadow: '0 0 0 8px rgba(255, 215, 0, 0.16)'
                        }
                      },
                      '& .MuiSlider-mark': {
                        bgcolor: 'rgba(255, 255, 255, 0.3)',
                        height: 10,
                        width: 2
                      }
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#C0C0C0', mb: 1 }}>
                    Conflict Window: {configuration.conflictWindow} hours
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Slider
                      value={configuration.conflictWindow}
                      onChange={(e, value) => handleConfigChange('conflictWindow', value)}
                      min={1}
                      max={6}
                      step={0.5}
                      size="small"
                      sx={{
                        color: '#F9B612',
                        flex: 1,
                        '& .MuiSlider-track': { 
                          bgcolor: '#F9B612',
                          height: 4
                        },
                        '& .MuiSlider-rail': { 
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                          height: 4
                        },
                        '& .MuiSlider-thumb': {
                          bgcolor: '#F9B612',
                          width: 16,
                          height: 16,
                          '&:hover': {
                            boxShadow: '0 0 0 6px rgba(249, 182, 18, 0.16)'
                          }
                        }
                      }}
                    />
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Chip 
                        label={`<${configuration.conflictWindow}h`} 
                        size="small" 
                        sx={{ bgcolor: '#FF5252', color: 'white', fontSize: '0.65rem', height: 20 }} 
                      />
                      <Chip 
                        label={`+29m`} 
                        size="small" 
                        sx={{ bgcolor: '#FF8C00', color: 'white', fontSize: '0.65rem', height: 20 }} 
                      />
                      <Chip 
                        label={`+59m`} 
                        size="small" 
                        sx={{ bgcolor: '#F9B612', color: 'black', fontSize: '0.65rem', height: 20 }} 
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={3}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#C0C0C0', mb: 1 }}>
                    Max Per Belt Per Hour: {configuration.maxFlightsPerHour}
                  </Typography>
                  <Slider
                    value={configuration.maxFlightsPerHour}
                    onChange={(e, value) => handleConfigChange('maxFlightsPerHour', value)}
                    min={1}
                    max={10}
                    marks
                    size="small"
                    sx={{
                      color: '#F9B612',
                      '& .MuiSlider-track': { 
                        background: 'linear-gradient(to right, #FFD700, #FFA500)',
                        border: 'none'
                      },
                      '& .MuiSlider-rail': { 
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        height: 6
                      },
                      '& .MuiSlider-thumb': {
                        bgcolor: '#FFD700',
                        width: 20,
                        height: 20,
                        '&:hover': {
                          boxShadow: '0 0 0 8px rgba(255, 215, 0, 0.16)'
                        }
                      },
                      '& .MuiSlider-mark': {
                        bgcolor: 'rgba(255, 255, 255, 0.3)',
                        height: 10,
                        width: 2
                      }
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={2}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={reprocessFlights}
                  startIcon={<RestartAltIcon />}
                  fullWidth
                  sx={{
                    bgcolor: '#F9B612',
                    color: '#000',
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#FFA500' }
                  }}
                >
                  Reprocess
                </Button>
              </Grid>
            </Grid>

            {/* Color Legend */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              mt: 1.5,
              pt: 1.5,
              borderTop: '1px solid rgba(255, 255, 255, 0.06)',
              flexWrap: 'wrap'
            }}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.6rem' }}>
                Legend
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: '#FF5252' }} />
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.65rem' }}>
                  Dest conflict (&lt;{configuration.conflictWindow}h)
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: '#FF8C00' }} />
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.65rem' }}>
                  Dest conflict (+29m)
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: '#F9B612' }} />
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.65rem' }}>
                  Dest conflict (+59m)
                </Typography>
              </Box>
              {configuration.destinationBeltGroups && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '2px',
                      border: '2px solid #FF8C00',
                      bgcolor: 'rgba(255, 140, 0, 0.2)'
                    }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.65rem' }}>
                      Wrong pod — flight routed to different pod
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '2px',
                      border: '2px solid rgba(255, 0, 0, 0.6)',
                      bgcolor: 'rgba(255, 0, 0, 0.25)'
                    }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.65rem' }}>
                      Cross-pod drop (while dragging)
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Paper>

          {error && (
            <Alert
              severity="error"
              onClose={() => setError('')}
              sx={{
                m: 2,
                mb: 0,
                bgcolor: 'rgba(211, 47, 47, 0.1)',
                color: '#ff6b6b',
                border: '1px solid rgba(211, 47, 47, 0.3)',
                '& .MuiAlert-icon': {
                  color: '#ff6b6b'
                }
              }}
            >
              {error}
            </Alert>
          )}

          {warning && (
            <Alert
              severity="warning"
              onClose={() => setWarning('')}
              sx={{
                m: 2,
                mb: 0,
                bgcolor: 'rgba(255, 152, 0, 0.1)',
                color: '#ffb74d',
                border: '1px solid rgba(255, 152, 0, 0.3)',
                '& .MuiAlert-icon': {
                  color: '#ffb74d'
                }
              }}
            >
              {warning}
            </Alert>
          )}

          {/* Main Content */}
          <Box sx={{ flexGrow: 1, overflow: 'hidden', p: 2 }}>
            {activeTab === 0 && <BeltGrid searchTerm={searchTerm} />}
            {activeTab === 1 && <ConflictsView />}
            {activeTab === 2 && <HourlyDistributionView />}
            {activeTab === 3 && <AssignmentAuditView />}
          </Box>
        </>
      )}

      {/* Progress Dialog */}
      <OptimizationProgress
        open={showProgress}
        onClose={() => setShowProgress(false)}
        mode={progressMode}
      />
    </Box>
  );
};

export default PiersView;